import { useEffect, useState } from 'react'
import { Alert, Stack } from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import MosSmartDialog from '../../common/MosSmartDialog'
import createDialogTitle from '../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { apiPost } from '../../../../generic/Api_Functions'
import { buildReturnPayload } from '../utils/returnPayload'

// Story 100, S29-S32 (RN19/RN20/RN23/POL3): the ONE modal that confirms the real date of BOTH the
// dispatch and the return, precharged with the click instant and editable. It never registers
// anything until the user explicitly confirms (S29): the network call only happens inside
// `confirm`, never on open.
//
// DN-40 (hub-arbitrated v2 contract, adopted 2026-09-02): confirming the date, in RETURN mode, is
// a SINGLE user gesture and a SINGLE call -- `POST mos/externalJob/return`. Sun resolves the
// returnable population itself, AT THE INSTANT OF THE ACT, so there is no `GET returnablePopulation`
// fetch here anymore (see `utils/returnPayload.js`). `nokPartIds` stays always empty (DECISIÓN PO
// 2026-09-01: MOS reports every returned part as OK, there is no NOK selection UI). A rejection
// (e.g. a partial-report guard on the server) keeps this SAME modal open, same verbatim-error
// mechanism as dispatch. `ReportQuantityDialog` (the original build's choice) is not reused: its
// quantity-against-one-quality-type shape could never have produced this body, and there is no
// report step left to mount it in.
const RESOURCE_BY_MODE = {
  dispatch: 'mos/externalJob/dispatch',
  return: 'mos/externalJob/return'
}

const TITLE_KEY_BY_MODE = {
  dispatch: 'Confirm dispatch date',
  return: 'Confirm return date'
}

const ONE_MINUTE_MS = 60 * 1000

// DN-11: capture and compare at MINUTE precision -- seconds/milliseconds never survive a pick.
const truncateToMinute = (date) => {
  const truncated = new Date(date)
  truncated.setSeconds(0, 0)
  return truncated
}

const ExternalJobDateConfirmModal = ({ open, mode = 'dispatch', row = {}, deviceId, onCancel = () => {}, onConfirmed = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const { format, ampm } = useDateFormatter()
  const [value, setValue] = useState(new Date())
  const [rangeReason, setRangeReason] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // S29: every time the modal (re)opens, the field precharges the CLICK instant -- not a stale
  // value left over from a previous open.
  useEffect(() => {
    if (open) {
      setValue(truncateToMinute(new Date()))
      setRangeReason('')
      setError('')
    }
  }, [open])

  // RN20 (both modes): never later than the server's "now" + 1 min of clock tolerance -- approximated
  // here with the CLIENT's clock, since RN23 makes this prevention only, never the guarantee.
  const maxDateTime = truncateToMinute(new Date(Date.now() + ONE_MINUTE_MS))
  // RN21 (dispatch only, S30): never earlier than the trip's known dispatch floor. The symmetric
  // floor for a RETURN (RN22, "not earlier than its own dispatch") is deferred: no scenario in this
  // batch exercises it, and `row.actualDispatch` is not yet a reliable source for it (S33 opens the
  // return modal from the ACTIONS surface, not yet passing that value through).
  const minDateTime = mode === 'dispatch' && row.dispatchFloor ? truncateToMinute(new Date(row.dispatchFloor)) : undefined

  const handleChange = (newValue) => {
    if (!newValue) return
    const truncated = truncateToMinute(newValue)
    if (truncated.getTime() > maxDateTime.getTime()) {
      setValue(maxDateTime)
      setRangeReason(t_('The date cannot be in the future'))
      return
    }
    if (minDateTime && truncated.getTime() < minDateTime.getTime()) {
      setValue(minDateTime)
      setRangeReason(t_('The dispatch date cannot be earlier than the previous step'))
      return
    }
    setRangeReason('')
    setValue(truncated)
  }

  const submitDispatch = async () => {
    setLoading(true)
    try {
      await apiPost(RESOURCE_BY_MODE.dispatch, { fabricationOrderId: row.fabricationOrder?.id, jobReferenceId: row.job?.id, deviceId, at: value })
      setError('')
      onConfirmed(value)
    } catch (err) {
      // S31/S32: the modal stays open (this component never flips `open` on failure -- only the
      // parent's `onConfirmed`/`onCancel` do) and shows the SERVER's own message VERBATIM, relying
      // on `Api_Functions` already keeping the full response body in `error.message` (habilitador
      // documented in story #89, reused here rather than rebuilt).
      setError(err.message || '')
    } finally {
      setLoading(false)
    }
  }

  // S33 (DECISIÓN PO 2026-09-01): every part comes back OK -- there is nothing to ask the operator.
  // DN-40: makes the ONE write; Sun resolves the returnable population itself, at the instant of
  // the act. On rejection, THIS is the request the server rejects (e.g. RN22, return before
  // dispatch) -- the modal stays open, same verbatim mechanism.
  const submitReturn = async () => {
    setLoading(true)
    try {
      await apiPost(RESOURCE_BY_MODE.return, buildReturnPayload(row, value))
      setError('')
      onConfirmed(value)
    } catch (err) {
      setError(err.message || '')
    } finally {
      setLoading(false)
    }
  }

  const accept = () => (mode === 'dispatch' ? submitDispatch() : submitReturn())

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_(TITLE_KEY_BY_MODE[mode]) })}
      setOpen={open}
      cancelCallback={onCancel}
      acceptCallback={accept}
      acceptLabel='Confirm'
      cancelLabel='Cancel'
      disableButtons={loading}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {error && <Alert severity='error'>{error}</Alert>}
        {rangeReason && <Alert severity='warning'>{rangeReason}</Alert>}
        <DateTimePicker
          ampm={ampm}
          label={t_('Date and time')}
          format={format?.dateTime}
          value={value}
          minDateTime={minDateTime}
          maxDateTime={maxDateTime}
          onChange={handleChange}
          slotProps={{ textField: { size: 'small' } }}
        />
      </Stack>
    </MosSmartDialog>
  )
}

export default ExternalJobDateConfirmModal
